//Dependencies............................................
import { Tabs, Form, Dropdown, Button, Space, Table, Input, InputNumber, Modal, Collapse, Tooltip, Typography, TableColumnsType } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { EditOutlined, ExclamationOutlined } from '@ant-design/icons';

//Components..............................................
import DeleteModal from "../../../../GlobalComponents/Modals/DeleteModal";
import { storedToDisplay } from '../../../../../utils/constants';

const { Text } = Typography;

type RuleType = 'CAN' | 'LIN';

// Renders (and edits) only its own type's rules, filtered out of the single
// shared `rules[]` array (formData). Every read/write below goes through the
// filtered-to-full index mapping below so CAN and LIN edits merge back into
// one array without disturbing each other's entries or their positions -
// that shared array + position is what the JSON config, the schema, and the
// binary generator all key off of (functionality_control's `rule` indices
// point straight into it).
export default  function Conf30RulesField({
    onChange,
    formData,
    errorSchema,
    formContext,
    ruleType,
    }   :   {
    formData: any,
    onChange: (newFormData: any) => void,
    errorSchema: any,
    formContext: any,
    ruleType: RuleType,
    })
{
    const allRules: Array<{ [key: string]: any }> = useMemo(() => (Array.isArray(formData) ? formData : []), [formData]);
    const indexedRules = useMemo(() => allRules
        .map((rule, originalIndex) => ({ rule, originalIndex }))
        .filter(({ rule }) => (ruleType === 'LIN' ? rule.type === 'LIN' : rule.type !== 'LIN')),
        [allRules, ruleType]);

    const [selectedDescription, setselectedDescription] = useState<{ [key: number]: string }>({});
    const [selectedCanId, setSelectedCanId] = useState<{ [key: number]: string }>({});
    const [selectedFrameId, setSelectedFrameId] = useState<{ [key: number]: string }>({});
    const [selectedDlc, setSelectedDlc] = useState<{ [key: number]: number }>({});

    const [selectedTabName, setSelectedTabName] = useState<string | null>(null);
    const [selectedTabIndex, setSelectedTabIndex] = useState<number | null>(null);
    const [newTabName, setNewTabName] = useState<string>('');
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

    const canMessageConfig = useMemo(() => formContext?.can_message_config || {}, [formContext]);
    const linMessageConfig = useMemo(() => formContext?.lin_message_config || {}, [formContext]);
    const canIdItems = Object.keys(canMessageConfig).map(canId => ({
        label: canId,
        key: canId,
    }));
    const frameIdItems = Object.keys(linMessageConfig).map(frameId => ({
        label: frameId,
        key: frameId,
    }));

    const frameBits = (frameId: string) => {
        const dataLen = linMessageConfig[frameId]?.data_len;
        return (dataLen ? dataLen : 8) * 8;
    };

    // Renders the frame's data bytes as a grid of individual bit cells (MSB
    // to LSB within each byte, byte 0 first), highlighting whichever bits
    // fall inside [bit_number, bit_number + bit_length) for this rule.
    const renderBitGrid = (frameId: string, bitNumber: number, bitLength: number) => {
        if (!frameId) return null;
        const dataLen = linMessageConfig[frameId]?.data_len || 8;
        const selectedStart = bitNumber;
        const selectedEnd = bitNumber + bitLength - 1;

        return (
            <div style={{ marginTop: 12, padding: 16, border: '1px solid #f0f0f0', borderRadius: 8, background: '#fafafa' }}>
                {Array.from({ length: dataLen }, (_, byteIndex) => byteIndex).map((byteIndex) => (
                    <div key={byteIndex} style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ width: 64, fontSize: 12, color: '#888', flexShrink: 0 }}>Byte {byteIndex}</span>
                        <Space size={6}>
                            {Array.from({ length: 8 }, (_, i) => 7 - i).map((bitInByte) => {
                                const globalBit = byteIndex * 8 + bitInByte;
                                const isSelected = globalBit >= selectedStart && globalBit <= selectedEnd;
                                return (
                                    <div
                                        key={globalBit}
                                        title={`bit ${globalBit}`}
                                        style={{
                                            width: 26,
                                            height: 26,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 10,
                                            borderRadius: 4,
                                            border: `1px solid ${isSelected ? '#1677ff' : '#d9d9d9'}`,
                                            background: isSelected ? '#1677ff' : '#fff',
                                            color: isSelected ? '#fff' : '#999',
                                            fontWeight: isSelected ? 600 : 400,
                                        }}
                                    >
                                        {globalBit}
                                    </div>
                                );
                            })}
                        </Space>
                    </div>
                ))}
            </div>
        );
    };

    useEffect(() => {
        indexedRules.forEach(({ rule }, localIndex) => {
            setselectedDescription(prevState => ({
                ...prevState,
                [localIndex]: rule.description || '',
            }));
            if (ruleType === 'LIN') {
                setSelectedFrameId(prevState => ({
                    ...prevState,
                    [localIndex]: rule.frame_id || '',
                }));
            } else {
                setSelectedCanId(prevState => ({
                    ...prevState,
                    [localIndex]: rule.message_id || '',
                }));
                const matchingMessage = canMessageConfig[rule.message_id];
                if (matchingMessage) {
                    setSelectedDlc(prevState => ({
                        ...prevState,
                        [localIndex]: matchingMessage.dlc,
                    }));
                }
            }
        });
    }, [formData]);// eslint-disable-line react-hooks/exhaustive-deps

    // Keeps each rule's type-specific fields in range as its referenced
    // CAN message / LIN frame changes: resizes the CAN mask/range arrays to
    // the message's DLC, and clamps LIN bit_number/bit_length/expected_value
    // back into the frame's data_len*8 bits.
    useEffect(() => {
        if (indexedRules.length === 0) return;
        let changed = false;
        const updatedFull = [...allRules];
        indexedRules.forEach(({ rule, originalIndex }) => {
            if (ruleType === 'LIN') {
                if (!rule.frame_id) return;
                const maxBits = frameBits(rule.frame_id);
                let { bit_number, bit_length, expected_value } = rule;
                let ruleChanged = false;
                if (bit_number === undefined || bit_number === null) bit_number = 0;
                if (bit_length === undefined || bit_length === null) bit_length = 1;
                if (bit_number > maxBits - 1) { bit_number = Math.max(0, maxBits - 1); ruleChanged = true; }
                if (bit_length > maxBits - bit_number) { bit_length = Math.max(1, maxBits - bit_number); ruleChanged = true; }
                const maxValue = (1 << bit_length) - 1;
                if (expected_value > maxValue) { expected_value = maxValue; ruleChanged = true; }
                if (ruleChanged) {
                    updatedFull[originalIndex] = { ...rule, bit_number, bit_length, expected_value };
                    changed = true;
                }
                return;
            }

            const matchingMessage = canMessageConfig[rule.message_id];
            if (!matchingMessage) return;
            const dlcCode = matchingMessage.dlc;
            const newDlc = storedToDisplay[dlcCode] ?? dlcCode;
            let ruleChanged = false;
            const updatedRule = { ...rule };
            ['mask', 'range_low', 'range_high'].forEach(maskKey => {
                const currentMask = updatedRule[maskKey] || [];
                if (currentMask.length > newDlc) {
                    updatedRule[maskKey] = currentMask.slice(0, newDlc);
                    ruleChanged = true;
                } else if (currentMask.length < newDlc) {
                    updatedRule[maskKey] = [
                        ...currentMask,
                        ...Array(newDlc - currentMask.length).fill('0x00'),
                    ];
                    ruleChanged = true;
                }
            });
            if (ruleChanged) {
                updatedFull[originalIndex] = updatedRule;
                changed = true;
            }
        });
        if (changed) onChange(updatedFull);
    }, [formContext]);// eslint-disable-line react-hooks/exhaustive-deps

    const updateRuleField = (localIndex: number, key: string, value: any) => {
        const originalIndex = indexedRules[localIndex].originalIndex;
        const updatedFull = [...allRules];
        updatedFull[originalIndex] = { ...updatedFull[originalIndex], [key]: value };
        onChange(updatedFull);
    };

    const updateMask = (key: string, localIndex: number, colIndex: number, inputValue: string) => {
        const originalIndex = indexedRules[localIndex].originalIndex;
        const updatedFull = [...allRules];
        const updatedRule = { ...updatedFull[originalIndex] };

        updatedRule[key] = updatedRule[key].map((val: string, i: number) =>
            i === colIndex ? inputValue : val
        );

        updatedFull[originalIndex] = updatedRule;
        onChange(updatedFull);
    };

    const handleCanIdSelect = (localIndex: number, canId: string) => {
        setSelectedCanId(prevState => ({
            ...prevState,
            [localIndex]: canId,
        }));

        const originalIndex = indexedRules[localIndex].originalIndex;
        const updatedFull = [...allRules];
        updatedFull[originalIndex] = { ...updatedFull[originalIndex], message_id: canId };

        const matchingMessage = canMessageConfig[canId];
        if (matchingMessage) {
            const dlcCode = matchingMessage.dlc;
            const newDlc = storedToDisplay[dlcCode] ?? dlcCode;
            setSelectedDlc(prevState => ({
                ...prevState,
                [localIndex]: dlcCode,
            }));

            ['mask', 'range_low', 'range_high'].forEach(maskKey => {
                const currentMask = updatedFull[originalIndex][maskKey] || [];
                if (currentMask.length > newDlc) {
                    updatedFull[originalIndex][maskKey] = currentMask.slice(0, newDlc);
                } else if (currentMask.length < newDlc) {
                    updatedFull[originalIndex][maskKey] = [
                        ...currentMask,
                        ...Array(newDlc - currentMask.length).fill('0x00'),
                    ];
                }
            });
        }
        onChange(updatedFull);
    };

    const handleFrameIdSelect = (localIndex: number, frameId: string) => {
        setSelectedFrameId(prevState => ({
            ...prevState,
            [localIndex]: frameId,
        }));
        updateRuleField(localIndex, 'frame_id', frameId);
    };

    const handleAddTab = () => {
        const newRule = ruleType === 'LIN'
            ? { type: 'LIN', description: 'Rename tab!', frame_id: '', bit_number: 0, bit_length: 1, expected_value: 0 }
            : { type: 'CAN', description: 'Rename tab!', message_id: '', mask: [], range_low: [], range_high: [] };
        onChange([...allRules, newRule]);
    };

    const reindexState = (state: { [key: number]: any }, index: number) =>
        Object.keys(state)
            .filter(key => Number(key) !== index)
            .reduce((acc, key, i) => {
                acc[i] = state[Number(key)];
                return acc;
            }, {} as { [key: number]: any });

    const renderMaskTable = (rule: any, originalIndex: number, localIndex: number) => {
        const dlcCode = selectedDlc[localIndex] || 0;
        const dlc = storedToDisplay[dlcCode] ?? dlcCode;
        const errors = (errorSchema as any)?.rules?.[String(originalIndex)] || {};

        const columns: TableColumnsType = [
            {
                title: 'Mask Type',
                dataIndex: 'maskType',
                fixed: 'left',
                width: 100,
                render: (text: string) => <span>{text}</span>,
            },
            ...Array(dlc).fill([]).map((_, colIndex) => ({
                title: `Byte ${colIndex + 1}`,
                width: 100,
                dataIndex: colIndex,
                render: (text: string, record: any) => {
                    const hasError = errors?.[record.key]?.[colIndex];
                    return(
                        <Input
                            value={record[colIndex]}
                            onChange={(e) => updateMask(record.key, localIndex, colIndex, e.target.value)}
                            style={{ borderColor: hasError ? 'red' : undefined }}
                        />
                    )
                }
            })),
        ];

        const data = [
            { key: 'mask', maskType: 'Mask', ...rule.mask },
            { key: 'range_low', maskType: 'Range LOW', ...rule.range_low },
            { key: 'range_high', maskType: 'Range HIGH', ...rule.range_high },
        ];

        return (
            <Table
                columns={columns}
                dataSource={data}
                pagination={false}
                bordered
                size="small"
                scroll={{ x: 'max-content' }}
            />
        );
    };

    const showRenameModal = (localIndex: number) => {
        setSelectedTabName(String(localIndex));
        setNewTabName(selectedDescription[localIndex]);
        setIsEditing(true);
    };

    const handleRenameTab = () => {
        if (selectedTabName && newTabName) {
            const localIndex = Number(selectedTabName);
            updateRuleField(localIndex, 'description', newTabName);
            setselectedDescription(prevState => ({
                ...prevState,
                [localIndex]: newTabName,
            }));
        }
        setIsEditing(false);
        setSelectedTabName(null);
        setNewTabName('');
    };

    return (
        <>
            <Collapse>
                <Collapse.Panel header={ruleType === 'LIN' ? 'LIN Rules' : 'CAN Rules'} key="1">
                    <Tabs
                        type="editable-card"
                        onEdit={(key, action) => {
                        if (action === 'add') {
                            handleAddTab();
                        } else if (action === 'remove') {
                            setSelectedTabIndex(Number(key));
                            setShowDeleteModal(true);
                        }
                        }}
                        addIcon="+"
                        items={indexedRules.map(({ rule, originalIndex }, localIndex) => {
                            const hasErrors = !!(errorSchema as any)?.rules?.[String(originalIndex)];
                            const frameId = selectedFrameId[localIndex] || rule.frame_id;
                            const maxBits = frameId ? frameBits(frameId) : 64;
                            const bitNumber = rule.bit_number ?? 0;
                            const bitLength = rule.bit_length ?? 1;

                            return {
                                key: String(localIndex),
                                label: (
                                    <span>
                                        <>
                                            <Tooltip title="Rename Tab">
                                                    <EditOutlined
                                                        onClick={() => showRenameModal(localIndex)}
                                                        style={{ cursor: 'pointer', marginLeft: 5}}
                                                        size={50}
                                                    />
                                            </Tooltip>
                                            {'  '}{selectedDescription[localIndex] || `Rename tab!`}{'  '}
                                            {hasErrors && (
                                                <ExclamationOutlined
                                                    style={{ color: 'red', fontSize: 15 }}
                                                />)
                                            }
                                        </>
                                    </span>
                                ),
                                closable: true,
                                children: (
                                    <Form layout="vertical">
                                        {ruleType === 'CAN' ? (
                                            <>
                                                <Form.Item label="Message ID" style={{marginBottom:'40px'}}>
                                                    <Dropdown
                                                    menu={{
                                                        items: canIdItems,
                                                        selectable: true,
                                                        onClick: (e) => handleCanIdSelect(localIndex, e.key),
                                                    }}
                                                    >
                                                    <Button>
                                                        <Space>{selectedCanId[localIndex] || 'Select CAN ID'}</Space>
                                                    </Button>
                                                    </Dropdown>
                                                </Form.Item>
                                                {renderMaskTable(rule, originalIndex, localIndex)}
                                            </>
                                        ) : (
                                            <>
                                                <Form.Item label="LIN Frame ID" style={{marginBottom:'24px'}}>
                                                    <Dropdown
                                                    menu={{
                                                        items: frameIdItems,
                                                        selectable: true,
                                                        onClick: (e) => handleFrameIdSelect(localIndex, e.key),
                                                    }}
                                                    >
                                                    <Button>
                                                        <Space>{selectedFrameId[localIndex] || 'Select LIN Frame'}</Space>
                                                    </Button>
                                                    </Dropdown>
                                                </Form.Item>
                                                <div style={{ display: 'flex', gap: 32, marginBottom: 16 }}>
                                                    <Form.Item label="Start Bit (LSB0)" style={{ marginBottom: 0 }}>
                                                        <InputNumber
                                                            min={0}
                                                            max={Math.max(0, maxBits - 1)}
                                                            value={bitNumber}
                                                            onChange={(value) => updateRuleField(localIndex, 'bit_number', value ?? 0)}
                                                        />
                                                    </Form.Item>
                                                    <Form.Item label="Bit Length" style={{ marginBottom: 0 }}>
                                                        <InputNumber
                                                            min={1}
                                                            max={Math.max(1, Math.min(32, maxBits - bitNumber))}
                                                            value={bitLength}
                                                            onChange={(value) => updateRuleField(localIndex, 'bit_length', value ?? 1)}
                                                        />
                                                    </Form.Item>
                                                    <Form.Item label="Expected Value (equals)" style={{ marginBottom: 0 }}>
                                                        <InputNumber
                                                            min={0}
                                                            max={(1 << bitLength) - 1}
                                                            value={rule.expected_value ?? 0}
                                                            onChange={(value) => updateRuleField(localIndex, 'expected_value', value ?? 0)}
                                                        />
                                                    </Form.Item>
                                                </div>
                                                {frameId && (
                                                    <div>
                                                        <Text type="secondary" style={{ display: 'block' }}>
                                                            Bits {bitNumber}..{bitNumber + bitLength - 1} of frame {frameId} (LSB0)
                                                        </Text>
                                                        {renderBitGrid(frameId, bitNumber, bitLength)}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </Form>
                                ),
                            }
                        })}
                    />
                </Collapse.Panel>
            </Collapse>
            <Modal
                title="Rename Tab"
                open={isEditing}
                onOk={handleRenameTab}
                onCancel={() => {
                    setIsEditing(false);
                    setSelectedTabName(null);
                }}
            >
                <Input
                    value={newTabName}
                    onChange={(e) => setNewTabName(e.target.value)}
                />
            </Modal>
            <DeleteModal
                showModal={showDeleteModal}
                onDelete={() => {
                    const originalIndexToRemove = indexedRules[selectedTabIndex!].originalIndex;
                    const updatedFull = allRules.filter((_, i) => i !== originalIndexToRemove);
                    onChange(updatedFull);

                    setselectedDescription(prev => {
                        const newState = { ...prev };
                        delete newState[selectedTabIndex!];
                        return reindexState(newState, selectedTabIndex!);
                    });

                    setSelectedCanId(prev => {
                        const newState = { ...prev };
                        delete newState[selectedTabIndex!];
                        return reindexState(newState, selectedTabIndex!);
                    });

                    setSelectedFrameId(prev => {
                        const newState = { ...prev };
                        delete newState[selectedTabIndex!];
                        return reindexState(newState, selectedTabIndex!);
                    });

                    setSelectedDlc(prev => {
                        const newState = { ...prev };
                        delete newState[selectedTabIndex!];
                        return reindexState(newState, selectedTabIndex!);
                    });
                    setShowDeleteModal(false);
                    setSelectedTabIndex(null);
                }}
                onClose={() => {
                    setShowDeleteModal(false);
                    setSelectedTabIndex(null);
                }}
            />
        </>
    );
}
